import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { IReferral } from './referral-list.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarService } from 'core/services/avatar.service';
import { format, Locale } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { UserAvatarComponent } from '../../user-avatar/user-avatar.component';
import { AuthService } from 'core/services/auth.service';
import { MyNetworkTabs } from '../../../types/myNetwork-tabs.enum';
import { MyNetworkTabService } from 'core/services/my-network-tab.service';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';

@Component({
  selector: 'referral-list',
  standalone: true,
  imports: [TranslateModule, NzAvatarModule, NzCheckboxModule, UserAvatarComponent],
  templateUrl: './referral-list.component.html',
  styleUrl: './referral-list.component.scss'
})
export class ReferralListComponent implements OnInit {
  @Input() referrals: IReferral[];
  @Input() selectionEnabled = false;
  @Input() selectedReferralIds: ReadonlySet<number> = new Set<number>();
  @Output() referralClick: EventEmitter<number>;
  @Output() selectedReferralIdsChange = new EventEmitter<ReadonlySet<number>>();
  public tab: MyNetworkTabs;

  public get hideFeatures(): boolean {
    return this._tabService.shouldHideFeatures(this.isSpecialUser, this.tab);
  }

  public get isSpecialUser(): boolean {
    return this._authService.currentUser?.isSpecial ?? false;
  }

  constructor(
    private _translateService: TranslateService,
    private _avatarService: AvatarService,
    private _authService: AuthService,
    private _tabService: MyNetworkTabService,
    private _router: Router
  ) {
    this.referralClick = new EventEmitter<number>();
  }

  public ngOnInit(): void {
    this.initializeTab();
  }

  public onReferralClick(referral: IReferral): void {
    this.referralClick.emit(referral.id);
  }

  public onItemCheck(referralId: number, checked: boolean): void {
    const selectedReferralIds = new Set<number>(this.selectedReferralIds);

    if (checked) {
      selectedReferralIds.add(referralId);
    } else {
      selectedReferralIds.delete(referralId);
    }

    this.selectedReferralIdsChange.emit(selectedReferralIds);
  }

  public getAvatarUrl(image: string): string {
    return this._avatarService.getUserAvatar(image);
  }

  public getReferralInitials(referral: IReferral): string {
    const nameParts: string[] = [referral?.first_name, referral?.last_name];
    return nameParts.map((name: string) => name.charAt(0).toUpperCase()).join('');
  }

  public getReminderCount(referral: IReferral): string {
    if (this.hideFeatures) {
      return '';
    }
    const lastContactedDatePrefix = this.getLastContactedDate(referral.last_contacted_date) + ' - ';
    switch (referral?.reminders_count) {
      case 0:
        return lastContactedDatePrefix + this._translateService.instant('buildYourNetwork.invitationSent');
      case 1:
        return lastContactedDatePrefix + this._translateService.instant('referral.firstReminderSent');
      case 2:
        return lastContactedDatePrefix + this._translateService.instant('referral.secondReminderSent');
      case 3:
        return lastContactedDatePrefix + this._translateService.instant('referral.thirdReminderSent');
      default:
        return '';
    }
  }

  public getLastContactedDate(date: string): string {
    const locale: Locale = this._translateService.currentLang === 'es' ? es : enUS;
    const formattedDate: string = format(new Date(date), 'd MMM', { locale });
    return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  }

  private initializeTab(): void {
    const urlSegments = this._router.url.split('/');
    const networkIndex = urlSegments.findIndex(segment => segment === 'build-your-network');
    if (networkIndex !== -1) {
      let tabSegment: string;
      if (this.isSpecialUser) {
        const parentSegment = urlSegments[networkIndex + 1];
        const childSegment = urlSegments[networkIndex + 2];

        if (childSegment && Object.values(MyNetworkTabs).includes(childSegment as MyNetworkTabs)) {
          tabSegment = childSegment;
        } else if (parentSegment && Object.values(MyNetworkTabs).includes(parentSegment as MyNetworkTabs)) {
          tabSegment = parentSegment;
        }
      } else {
        tabSegment = urlSegments[networkIndex + 1];
      }

      const possibleTab = tabSegment as MyNetworkTabs;
      if (possibleTab && Object.values(MyNetworkTabs).includes(possibleTab)) {
        this.tab = possibleTab;
      } else {
        this.tab = MyNetworkTabs.GANARBOLS;
      }
    }
  }
}
